/**
 * VerifiedRoute.jsx
 * Protects routes that require a verified account.
 * - Not logged in → redirect to home
 * - Logged in but not verified → redirect to /verify with a message
 * - Verified → render children
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useApi } from '../context/AuthContext';

export default function VerifiedRoute({ children }) {
  const { isLoggedIn, loading: authLoading } = useAuth();
  const api = useApi();

  const [verifyStatus, setVerifyStatus] = useState(null); // null = loading
  const [checkDone, setCheckDone] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setCheckDone(true);
      return;
    }
    api.get('/users/me/verify/status')
      .then(r => setVerifyStatus(r.data?.verificationStatus || 'unverified'))
      .catch(() => setVerifyStatus('unverified'))
      .finally(() => setCheckDone(true));
  }, [isLoggedIn]);

  // Still loading auth or verification check
  if (authLoading || !checkDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Checking access…</p>
        </div>
      </div>
    );
  }

  // Not logged in → go home
  if (!isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  // Logged in but not verified → send to verify page
  if (verifyStatus !== 'verified') {
    return <Navigate to="/verify?reason=private-rentals" replace />;
  }

  return children;
}
